import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import Navbar from './components/Navbar';
import ResearchFocusAreas from './components/Research-Focus-Area.js';
import OngoingResearchProjects from './components/Ongoing-Research-Project.js';
import PublicationsList from './components/Research-Publications.js';
import UpcomingEvents from './components/Research-Upcoming-Conference.js';
import AITrainerKitResearch from './components/AI-Trainer-Kit-Research.js';
import { Users, Target, Lightbulb, Upload } from 'lucide-react';
import Footer from './components/Footer.js';

export default function AIResearchHub() {
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Modal states
  const [showFellowshipModal, setShowFellowshipModal] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  
  // Submission loading states for each form
  const [isFellowshipSubmitting, setIsFellowshipSubmitting] = useState(false);
  const [isProposalSubmitting, setIsProposalSubmitting] = useState(false);

  // Form data states
  const [fellowshipFormData, setFellowshipFormData] = useState({
    name: '',
    email: '',
    phone: '',
    designation: '',
    resume: null,
    questions: ''
  });

  const [proposalFormData, setProposalFormData] = useState({
    name: '',
    email: '',
    phone: '',
    resume: null,
    researchField: '',
    researchIdea: ''
  });

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Helper function to convert a file to a Base64 string
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]); // Get only the Base64 part
    reader.onerror = error => reject(error);
  });

  // Generic file input handler with size validation
  const handleFileInputChange = (e, formSetter) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

      if (file.size > MAX_FILE_SIZE) {
        alert('File size exceeds 10MB. Please upload a smaller file.');
        e.target.value = null; // Clear the input
        formSetter(prev => ({ ...prev, [name]: null }));
        return;
      }
      formSetter(prev => ({ ...prev, [name]: file }));
    }
  };

  // Fellowship Modal handlers
  const handleShowFellowshipModal = () => setShowFellowshipModal(true);
  const handleCloseFellowshipModal = () => {
    setShowFellowshipModal(false);
    setFellowshipFormData({ name: '', email: '', phone: '', designation: '', resume: null, questions: '' });
  };
  const handleFellowshipInputChange = (e) => {
    const { name, value } = e.target;
    setFellowshipFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFellowshipSubmit = async (e) => {
    e.preventDefault();
    if (!fellowshipFormData.name || !fellowshipFormData.email || !fellowshipFormData.resume) {
        alert("Please fill out all required fields.");
        return;
    }
    setIsFellowshipSubmitting(true);
    
    const GOOGLE_SCRIPT_ENDPOINT = 'https://script.google.com/macros/s/AKfycbxy5bdzpGH_iuiTniDfgmAq4FFNLRxnztUAm7jUzdWX-Xlvgje_oYHYHPnzU-6QXBi_/exec';

    try {
      const base64File = await fileToBase64(fellowshipFormData.resume);
      const submissionData = {
        ...fellowshipFormData,
        formType: 'fellowship', // Add a type to differentiate forms
        resume: {
          name: fellowshipFormData.resume.name,
          mimeType: fellowshipFormData.resume.type,
          data: base64File,
        },
      };

      const response = await fetch(GOOGLE_SCRIPT_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(submissionData),
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      });
      const result = await response.json();

      if (result.status === 'success') {
        alert('Thank you for your application! We will review your submission and contact you within 2-3 business days.');
        handleCloseFellowshipModal();
      } else {
        throw new Error(result.message || 'An unknown error occurred.');
      }
    } catch (error) {
      console.error('Submission failed:', error);
      alert(`An error occurred while submitting your application: ${error.message}`);
    } finally {
      setIsFellowshipSubmitting(false);
    }
  };

  // Proposal Modal handlers
  const handleShowProposalModal = () => setShowProposalModal(true);
  const handleCloseProposalModal = () => {
    setShowProposalModal(false);
    setProposalFormData({ name: '', email: '', phone: '', resume: null, researchField: '', researchIdea: '' });
  };
  const handleProposalInputChange = (e) => {
    const { name, value } = e.target;
    setProposalFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProposalSubmit = async (e) => {
    e.preventDefault();
    if (!proposalFormData.name || !proposalFormData.email || !proposalFormData.resume || !proposalFormData.researchIdea) {
        alert("Please fill out all required fields.");
        return;
    }
    setIsProposalSubmitting(true);

    const GOOGLE_SCRIPT_ENDPOINT = 'https://script.google.com/macros/s/AKfycbxy5bdzpGH_iuiTniDfgmAq4FFNLRxnztUAm7jUzdWX-Xlvgje_oYHYHPnzU-6QXBi_/exec';

    try {
      const base64File = await fileToBase64(proposalFormData.resume);
      const submissionData = {
        ...proposalFormData,
        formType: 'proposal', // Add a type to differentiate forms
        resume: {
          name: proposalFormData.resume.name,
          mimeType: proposalFormData.resume.type,
          data: base64File,
        },
      };

      const response = await fetch(GOOGLE_SCRIPT_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(submissionData),
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      });
      const result = await response.json();

      if (result.status === 'success') {
        alert('Thank you for your research proposal! Our research committee will review your submission and provide feedback within 2-3 days.');
        handleCloseProposalModal();
      } else {
        throw new Error(result.message || 'An unknown error occurred.');
      }
    } catch (error) {
      console.error('Submission failed:', error);
      alert(`An error occurred while submitting your proposal: ${error.message}`);
    } finally {
      setIsProposalSubmitting(false);
    }
  };

  // Styles (unchanged)
  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #031035ff 0%, #1a1d26ff 50%, #000000ff 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    fontFamily: 'Arial, sans-serif'
  };

  const contentStyle = {
    maxWidth: '1200px',
    textAlign: 'center',
    color: 'white',
    opacity: isLoaded ? 1 : 0,
    transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
    transition: 'all 1s ease-out'
  };

  const logoStyle = {
    width: '96px',
    height: '96px',
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 3rem auto',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    cursor: 'pointer',
    transition: 'transform 0.3s ease'
  };

  const headingStyle = {
    fontSize: '4rem',
    fontWeight: 'bold',
    margin: '0 0 2rem 0',
    background: 'linear-gradient(90deg, #60a5fa, #93c5fd, #67e8f9)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  };

  const descriptionStyle = {
    fontSize: '1.5rem',
    lineHeight: '1.6',
    color: '#cbd5e1',
    margin: '0 auto 3rem auto',
    maxWidth: '800px'
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: '1.5rem',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap'
  };

  const primaryButtonStyle = {
    padding: '1rem 2rem',
    background: 'linear-gradient(90deg, #2563eb, #1d4ed8)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'default',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    minWidth: '240px',
    justifyContent: 'center',
    boxShadow: '0 10px 25px -5px rgba(37, 99, 235, 0.3)',
    transition: 'all 0.3s ease'
  };

  const microscopeIconStyle = {
    width: '48px',
    height: '48px',
    fill: '#dbeafe'
  };

  const bookIconStyle = {
    width: '20px',
    height: '20px',
    fill: 'currentColor'
  };

  const handleLogoHover = (e) => {
    e.target.style.transform = 'scale(1.1)';
  };

  const handleLogoLeave = (e) => {
    e.target.style.transform = 'scale(1)';
  };

  const handleButtonHover = (e) => {
    e.target.style.transform = 'scale(1.05)';
  };

  const handleButtonLeave = (e) => {
    e.target.style.transform = 'scale(1)';
  };

  return (
    <>
      <div>
        <Navbar />
        <div style={containerStyle}>
          <div style={{ ...contentStyle, marginTop: '10em' }}>
            <div 
              style={logoStyle}
              onMouseEnter={handleLogoHover}
              onMouseLeave={handleLogoLeave}
            >
              <svg style={microscopeIconStyle} viewBox="0 0 24 24">
                <path d="M9.5 8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM8 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm8 8a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm0-2a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm-8 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm0-2a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"/>
              </svg>
            </div>
            <h1 style={headingStyle}>
              AI Research Hub
            </h1>
            <p style={descriptionStyle}>
              Advancing the frontiers of artificial intelligence through collaborative research,
              innovative methodologies, and global partnerships. Shaping the future of AI education
              and democratizing access to cutting-edge knowledge.
            </p>
            <div style={buttonContainerStyle}>
              <button 
                style={primaryButtonStyle}
                onMouseEnter={handleButtonHover}
                onMouseLeave={handleButtonLeave}
              >
                <svg style={bookIconStyle} viewBox="0 0 24 24">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20a2 2 0 0 1 2 2v1.5a.5.5 0 0 1-.5.5h-17a.5.5 0 0 1-.5-.5v-1.5zM6.5 2A2.5 2.5 0 0 0 4 4.5V15h16V4.5A2.5 2.5 0 0 0 17.5 2h-11z"/>
                </svg>
                Explore Research
              </button>
            </div>
          </div>
        </div>
        <ResearchFocusAreas />
        <OngoingResearchProjects />
        <PublicationsList />
        <UpcomingEvents />
        <AITrainerKitResearch />
        <div style={{
          backgroundColor: '#0a0a0a',
          minHeight: '60vh',
          padding: '80px 20px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            maxWidth: '1000px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <div style={{
              marginBottom: '40px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                border: '2px solid #3B82F6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                backgroundColor: 'transparent'
              }}>
                <Lightbulb size={40} color="#3B82F6" />
              </div>
            </div>
            <h1 style={{
              color: 'white',
              fontSize: '48px',
              fontWeight: '800',
              marginBottom: '24px',
              letterSpacing: '-0.02em',
              lineHeight: '1.2'
            }}>
              Join Our Research Community
            </h1>
            <p style={{
              color: '#94A3B8',
              fontSize: '18px',
              lineHeight: '1.6',
              marginBottom: '48px',
              maxWidth: '700px',
              margin: '0 auto 48px auto'
            }}>
              Collaborate with leading AI researchers, access cutting-edge resources, and contribute to the future of artificial intelligence education.
            </p>
            <div style={{
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <button 
                style={{
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px 32px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)'
                }}
                onClick={handleShowFellowshipModal}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563EB';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3B82F6';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 20px rgba(59, 130, 246, 0.3)';
                }}
              >
                <Users size={20} />
                Apply for Research Fellowship
              </button>
              <button 
                style={{
                  backgroundColor: 'transparent',
                  color: '#94A3B8',
                  border: '1px solid #374151',
                  borderRadius: '12px',
                  padding: '16px 32px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.3s ease'
                }}
                onClick={handleShowProposalModal}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#1F2937';
                  e.target.style.borderColor = '#4B5563';
                  e.target.style.color = 'white';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = '#374151';
                  e.target.style.color = '#94A3B8';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <Target size={20} />
                Submit Research Proposal
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* Research Fellowship Modal */}
      <Modal show={showFellowshipModal} onHide={handleCloseFellowshipModal} size="lg" centered>
        <Modal.Header closeButton style={{ 
          borderBottom: '1px solid #e2e8f0', 
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
        }}>
          <Modal.Title style={{ 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            🎓 Apply for Research Fellowship
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ padding: '2rem', background: '#ffffff' }}>
          <Form onSubmit={handleFellowshipSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Full Name *
                  </Form.Label>
                  <Form.Control type="text" name="name" value={fellowshipFormData.name} onChange={handleFellowshipInputChange} required placeholder="Enter your full name" style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Email Address *
                  </Form.Label>
                  <Form.Control type="email" name="email" value={fellowshipFormData.email} onChange={handleFellowshipInputChange} required placeholder="your.email@example.com" style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Phone Number *
                  </Form.Label>
                  <Form.Control type="tel" name="phone" value={fellowshipFormData.phone} onChange={handleFellowshipInputChange} required placeholder="+1 (555) 123-4567" style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Current Designation/Position *
                  </Form.Label>
                  <Form.Control type="text" name="designation" value={fellowshipFormData.designation} onChange={handleFellowshipInputChange} required placeholder="e.g., PhD Student, Research Associate" style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Resume/CV * (PDF, DOC, DOCX - Max 10MB)
                  </Form.Label>
                  <Form.Control type="file" name="resume" onChange={(e) => handleFileInputChange(e, setFellowshipFormData)} accept=".pdf,.doc,.docx" required style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  {fellowshipFormData.resume && (
                    <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f8fafc', borderRadius: '4px', fontSize: '0.8rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Upload size={14} />
                      {fellowshipFormData.resume.name} ({(fellowshipFormData.resume.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                Questions or Additional Information 
              </Form.Label>
              <Form.Control as="textarea" rows={4} name="questions" value={fellowshipFormData.questions} onChange={handleFellowshipInputChange} placeholder="Tell us about your research interests..." style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease', resize: 'vertical' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
            </Form.Group>
            <div style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #3B82F6' }}>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#1e40af', lineHeight: '1.4' }}>
                🎓 <strong>Fellowship Benefits:</strong> Mentorship from leading AI researchers, access to cutting-edge resources, and potential publication support.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <Button variant="outline-secondary" onClick={handleCloseFellowshipModal} style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', fontWeight: '600', border: '2px solid #e2e8f0', color: '#6b7280' }}>
                Cancel
              </Button>
              <Button type="submit" style={{ padding: '0.75rem 2rem', borderRadius: '8px', fontWeight: '600', background: 'linear-gradient(135deg, #3B82F6, #2563EB)', border: 'none', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)' }} disabled={isFellowshipSubmitting}>
                {isFellowshipSubmitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Research Proposal Modal */}
      <Modal show={showProposalModal} onHide={handleCloseProposalModal} size="lg" centered>
        <Modal.Header closeButton style={{ 
          borderBottom: '1px solid #e2e8f0', 
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
        }}>
          <Modal.Title style={{ 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            🔬 Submit Research Proposal
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ padding: '2rem', background: '#ffffff' }}>
          <Form onSubmit={handleProposalSubmit}>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Full Name *
                  </Form.Label>
                  <Form.Control type="text" name="name" value={proposalFormData.name} onChange={handleProposalInputChange} required placeholder="Enter your full name" style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Email Address *
                  </Form.Label>
                  <Form.Control type="email" name="email" value={proposalFormData.email} onChange={handleProposalInputChange} required placeholder="your.email@example.com" style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Phone Number *
                  </Form.Label>
                  <Form.Control type="tel" name="phone" value={proposalFormData.phone} onChange={handleProposalInputChange} required placeholder="+1 (555) 123-4567" style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Resume/CV * (PDF, DOC, DOCX - Max 10MB)
                  </Form.Label>
                  <Form.Control type="file" name="resume" onChange={(e) => handleFileInputChange(e, setProposalFormData)} accept=".pdf,.doc,.docx" required style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
                  {proposalFormData.resume && (
                    <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f8fafc', borderRadius: '4px', fontSize: '0.8rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Upload size={14} />
                      {proposalFormData.resume.name} ({(proposalFormData.resume.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Research Field *
                  </Form.Label>
                  <Form.Select name="researchField" value={proposalFormData.researchField} onChange={handleProposalInputChange} required style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}>
                    <option value="">Select Research Field</option>
                    <option value="machine-learning">Machine Learning</option>
                    <option value="deep-learning">Deep Learning</option>
                    <option value="natural-language-processing">Natural Language Processing</option>
                    <option value="computer-vision">Computer Vision</option>
                    <option value="robotics">Robotics</option>
                    <option value="ai-ethics">AI Ethics</option>
                    <option value="reinforcement-learning">Reinforcement Learning</option>
                    <option value="ai-education">AI Education</option>
                    <option value="human-ai-interaction">Human-AI Interaction</option>
                    <option value="ai-healthcare">AI in Healthcare</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                Research Idea/Proposal Overview *
              </Form.Label>
              <Form.Control as="textarea" rows={6} name="researchIdea" value={proposalFormData.researchIdea} onChange={handleProposalInputChange} required placeholder="Describe your research idea..." style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease', resize: 'vertical' }} onFocus={(e) => e.target.style.borderColor = '#3B82F6'} onBlur={(e) => e.target.style.borderColor = '#e2e8f0'} />
            </Form.Group>
            <div style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #0369a1' }}>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#0c4a6e', lineHeight: '1.4' }}>
                🔬 <strong>Review Process:</strong> Proposals are evaluated based on novelty, feasibility, and potential impact. Successful proposals may receive funding and mentorship.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <Button variant="outline-secondary" onClick={handleCloseProposalModal} style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', fontWeight: '600', border: '2px solid #e2e8f0', color: '#6b7280' }}>
                Cancel
              </Button>
              <Button type="submit" style={{ padding: '0.75rem 2rem', borderRadius: '8px', fontWeight: '600', background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', border: 'none', boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)' }} disabled={isProposalSubmitting}>
                {isProposalSubmitting ? 'Submitting...' : 'Submit Proposal'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}
